import type { VerortungGeruest } from '../types';

/**
 * Messprotokoll des Sensor-Messmodus (docs/maengel-verortung-plan.md 9.4,
 * Paket P5): Punkte, die der Nutzer auf der Baustelle SELBST setzt —
 * Nullpunkt, „Hier bin ich" (Geschoss-Kontrolle) und Passpunkte (GPS-Mittel).
 *
 * Zweck: vor dem Bau der Oberflaeche messen, wie oft das Barometer das
 * richtige Geschoss trifft und wie genau GPS auf der Decke ist.
 *
 * Datenschutz: nur die gesetzten Punkte, keine Spur; Ablage nur auf diesem
 * Geraet (localStorage), Weitergabe nur per „Als CSV teilen".
 */

export type MessTyp = 'nullpunkt' | 'kontrolle' | 'passpunkt';

export interface MessEintrag {
    id: string;
    typ: MessTyp;
    t: number;
    /** Vom Nutzer angegebenes Geschoss (Nullpunkt, Kontrolle). */
    geschoss?: string | null;
    /** Aus der Kamera-Vorschau: Geruest-Lage statt Geschoss (zaehlt nicht als gemessene Geschosshoehe). */
    ebeneArt?: 'geschoss' | 'geruest' | null;
    /** Name des Passpunkts. */
    name?: string | null;
    druckHpa?: number | null;
    streuungHpa?: number | null;
    /** Gemessene Hoehe relativ ±0,00. */
    hoeheM?: number | null;
    /** Kontrolle: geschaetztes Geschoss, „zwischen …", Sicherheit 0–1, Treffer. */
    geschaetzt?: string | null;
    zwischen?: string | null;
    sicherheit?: number | null;
    treffer?: boolean | null;
    nullpunktAlterMin?: number | null;
    /** Kontrolle: danach neu genullt? */
    neuGenullt?: boolean | null;
    lat?: number | null;
    lng?: number | null;
    genauigkeitM?: number | null;
    satelliten?: number | null;
    zweiFrequenz?: boolean | null;
    zone?: number | null;
    ost?: number | null;
    nord?: number | null;
    /** Passpunkt: Anzahl Fixe, Streuung, Soll-Koordinate und Abweichung. */
    anzahl?: number | null;
    streuungM?: number | null;
    sollOst?: number | null;
    sollNord?: number | null;
    abweichungM?: number | null;
    azimutGrad?: number | null;
    kompassGenauigkeit?: number | null;
}

export const PROTOKOLL_SCHLUESSEL = 'sigeko.sensorMessprotokoll.v1';
export const EINSTELLUNG_SCHLUESSEL = 'sigeko.sensorMessmodus.v1';
/** Obergrenze, damit ein vergessenes Protokoll den Speicher nicht fuellt. */
export const MAX_EINTRAEGE = 500;

export interface Trefferquote {
    /** Kontrollen mit Schaetzung (treffer true/false). */
    bewertet: number;
    treffer: number;
    /** Kontrollen, bei denen die Schaetzung „zwischen" meldete — zaehlen nicht als Treffer. */
    zwischen: number;
}

export function trefferquote(eintraege: readonly MessEintrag[]): Trefferquote {
    let bewertet = 0;
    let treffer = 0;
    let zwischen = 0;
    for (const e of eintraege) {
        if (e.typ !== 'kontrolle' || typeof e.treffer !== 'boolean') continue;
        bewertet += 1;
        if (e.treffer) treffer += 1;
        if (e.zwischen) zwischen += 1;
    }
    return { bewertet, treffer, zwischen };
}

/** Ist die Geschossangabe dieselbe? Gross/klein, Leerzeichen und Punkte egal („1.OG" = „1. og"). */
export function gleichesGeschoss(a: string | null | undefined, b: string | null | undefined): boolean {
    const norm = (s: string) => s.toLowerCase().replace(/[\s.]+/g, '');
    return !!a && !!b && norm(a) === norm(b);
}

// ─── CSV ─────────────────────────────────────────────────────────────────────
// Semikolon und Dezimalkomma: oeffnet so in deutschem Excel ohne Import-Dialog.

const SPALTEN: readonly { kopf: string; wert: (e: MessEintrag) => string }[] = [
    { kopf: 'zeit', wert: (e) => zeitText(e.t) },
    { kopf: 'typ', wert: (e) => e.typ },
    { kopf: 'geschoss_ist', wert: (e) => e.geschoss ?? '' },
    { kopf: 'geschoss_geschaetzt', wert: (e) => e.geschaetzt ?? '' },
    { kopf: 'zwischen', wert: (e) => e.zwischen ?? '' },
    { kopf: 'treffer', wert: (e) => (typeof e.treffer === 'boolean' ? (e.treffer ? 'ja' : 'nein') : '') },
    { kopf: 'sicherheit', wert: (e) => zahlText(e.sicherheit, 2) },
    { kopf: 'hoehe_m', wert: (e) => zahlText(e.hoeheM, 2) },
    { kopf: 'druck_hpa', wert: (e) => zahlText(e.druckHpa, 3) },
    { kopf: 'streuung_hpa', wert: (e) => zahlText(e.streuungHpa, 3) },
    { kopf: 'nullpunkt_alter_min', wert: (e) => zahlText(e.nullpunktAlterMin, 1) },
    { kopf: 'neu_genullt', wert: (e) => (typeof e.neuGenullt === 'boolean' ? (e.neuGenullt ? 'ja' : 'nein') : '') },
    { kopf: 'passpunkt', wert: (e) => e.name ?? '' },
    { kopf: 'lat', wert: (e) => zahlText(e.lat, 7) },
    { kopf: 'lng', wert: (e) => zahlText(e.lng, 7) },
    { kopf: 'gps_genauigkeit_m', wert: (e) => zahlText(e.genauigkeitM, 1) },
    { kopf: 'satelliten', wert: (e) => zahlText(e.satelliten, 0) },
    { kopf: 'zweifrequenz', wert: (e) => (typeof e.zweiFrequenz === 'boolean' ? (e.zweiFrequenz ? 'ja' : 'nein') : '') },
    { kopf: 'utm_zone', wert: (e) => zahlText(e.zone, 0) },
    { kopf: 'utm_ost', wert: (e) => zahlText(e.ost, 2) },
    { kopf: 'utm_nord', wert: (e) => zahlText(e.nord, 2) },
    { kopf: 'fixe', wert: (e) => zahlText(e.anzahl, 0) },
    { kopf: 'streuung_m', wert: (e) => zahlText(e.streuungM, 2) },
    { kopf: 'soll_ost', wert: (e) => zahlText(e.sollOst, 2) },
    { kopf: 'soll_nord', wert: (e) => zahlText(e.sollNord, 2) },
    { kopf: 'abweichung_m', wert: (e) => zahlText(e.abweichungM, 2) },
    { kopf: 'kompass_grad', wert: (e) => zahlText(e.azimutGrad, 0) },
    { kopf: 'kompass_genauigkeit', wert: (e) => zahlText(e.kompassGenauigkeit, 0) },
];

function zweistellig(n: number): string {
    return String(n).padStart(2, '0');
}

/** Ortszeit „2026-09-27 14:03:12" — lesbar und sortierbar. */
export function zeitText(t: number): string {
    const d = new Date(t);
    return `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())} `
        + `${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}:${zweistellig(d.getSeconds())}`;
}

function zahlText(w: number | null | undefined, stellen: number): string {
    if (typeof w !== 'number' || !Number.isFinite(w)) return '';
    return w.toFixed(stellen).replace('.', ',');
}

function zelle(text: string): string {
    return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * CSV mit Kopfzeilen „# …" (Geraet, Sensor, Geschossliste), dann Spaltenkopf
 * und Eintraege in zeitlicher Reihenfolge. Zeilenende CRLF, mit BOM fuer Excel.
 */
export function protokollAlsCsv(eintraege: readonly MessEintrag[], kopf: readonly string[] = []): string {
    const zeilen: string[] = [];
    for (const k of kopf) zeilen.push(`# ${k.replace(/[\r\n]+/g, ' ')}`);
    zeilen.push(SPALTEN.map((s) => s.kopf).join(';'));
    const sortiert = [...eintraege].sort((a, b) => a.t - b.t);
    for (const e of sortiert) zeilen.push(SPALTEN.map((s) => zelle(s.wert(e))).join(';'));
    return `﻿${zeilen.join('\r\n')}\r\n`;
}

// ─── Ablage (nur dieses Geraet) ──────────────────────────────────────────────

interface Speicher {
    getItem(k: string): string | null;
    setItem(k: string, v: string): void;
    removeItem(k: string): void;
}

const standardSpeicher = (): Speicher | null => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

const MESS_TYPEN: readonly MessTyp[] = ['nullpunkt', 'kontrolle', 'passpunkt'];

/** Gespeichertes Protokoll; Kaputtes wird still verworfen (nie werfen). */
export function protokollLaden(speicher: Speicher | null = standardSpeicher()): MessEintrag[] {
    if (!speicher) return [];
    try {
        const roh: unknown = JSON.parse(speicher.getItem(PROTOKOLL_SCHLUESSEL) ?? '[]');
        if (!Array.isArray(roh)) return [];
        return roh.filter((e): e is MessEintrag =>
            !!e && typeof e === 'object'
            && typeof (e as MessEintrag).id === 'string'
            && typeof (e as MessEintrag).t === 'number'
            && MESS_TYPEN.includes((e as MessEintrag).typ),
        ).slice(-MAX_EINTRAEGE);
    } catch {
        return [];
    }
}

/** Speichern; voller Speicher o. Ae. ist kein Grund, die Messung abzubrechen → false. */
export function protokollSpeichern(eintraege: readonly MessEintrag[], speicher: Speicher | null = standardSpeicher()): boolean {
    if (!speicher) return false;
    try {
        if (eintraege.length === 0) speicher.removeItem(PROTOKOLL_SCHLUESSEL);
        else speicher.setItem(PROTOKOLL_SCHLUESSEL, JSON.stringify(eintraege.slice(-MAX_EINTRAEGE)));
        return true;
    } catch {
        return false;
    }
}

export type GeschossQuelle = 'eingabe' | 'messung';

export interface MessEinstellung {
    /** Freitext der Soll-Geschosshoehen („EG 0; 1. OG 3,05 …"). */
    geschosseText: string;
    /** Welche Liste die Schaetzung nutzt: eingetragen (aus dem Plan) oder vor Ort gemessen. */
    quelle: GeschossQuelle;
    /** Geruest fuer die Kamera-Vorschau (null = keins). */
    geruest?: VerortungGeruest | null;
}

export const STANDARD_EINSTELLUNG: MessEinstellung = { geschosseText: '', quelle: 'eingabe', geruest: null };

/** Geruest aus gespeicherten Daten; Unbrauchbares → null. */
export function geruestLesen(roh: unknown): VerortungGeruest | null {
    if (!roh || typeof roh !== 'object') return null;
    const o = roh as Record<string, unknown>;
    const zahl = (w: unknown) => (typeof w === 'number' && Number.isFinite(w) ? w : null);
    const ersteLageM = zahl(o.ersteLageM);
    const abstandM = zahl(o.abstandM);
    const lagen = zahl(o.lagen);
    if (ersteLageM === null || abstandM === null || lagen === null || abstandM <= 0 || lagen < 1) return null;
    return { ersteLageM, abstandM, lagen: Math.round(lagen) };
}

export function einstellungLaden(speicher: Speicher | null = standardSpeicher()): MessEinstellung {
    if (!speicher) return STANDARD_EINSTELLUNG;
    try {
        const roh: unknown = JSON.parse(speicher.getItem(EINSTELLUNG_SCHLUESSEL) ?? 'null');
        if (!roh || typeof roh !== 'object') return STANDARD_EINSTELLUNG;
        const o = roh as Partial<MessEinstellung>;
        return {
            geschosseText: typeof o.geschosseText === 'string' ? o.geschosseText : '',
            quelle: o.quelle === 'messung' ? 'messung' : 'eingabe',
            geruest: geruestLesen(o.geruest),
        };
    } catch {
        return STANDARD_EINSTELLUNG;
    }
}

export function einstellungSpeichern(e: MessEinstellung, speicher: Speicher | null = standardSpeicher()): void {
    if (!speicher) return;
    try {
        speicher.setItem(EINSTELLUNG_SCHLUESSEL, JSON.stringify(e));
    } catch {
        // Nur Komfort — ohne Ablage bleibt die Eingabe bis zum Schliessen erhalten.
    }
}
